import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { CommunityVideo, VideoCategory } from '../../../api/community/types';
import { communityService } from '../../../api/community/communityService';
import ActionCard from './ActionCard';
import LevelUpCard from './LevelUpCard';
import SocialCard from './SocialCard';
import './CommunitySection.css';

interface CommunitySectionProps {
  category: VideoCategory;
  categoryLabel: string;
  onViewVideo?: (video: CommunityVideo) => void;
  onEditVideo?: (video: CommunityVideo) => void;
  onPublishVideo?: (video: CommunityVideo) => void;
  onUnpublishVideo?: (video: CommunityVideo) => void;
  onDeleteVideo?: (video: CommunityVideo) => void;
  onAddNew?: (category: VideoCategory) => void;
  refreshKey?: number; // Key to trigger refresh
}

const CommunitySection: React.FC<CommunitySectionProps> = ({
  category,
  categoryLabel,
  onViewVideo,
  onEditVideo,
  onPublishVideo,
  onUnpublishVideo,
  onDeleteVideo,
  onAddNew,
  refreshKey
}) => {
  const [videos, setVideos] = useState<CommunityVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [totalPages, setTotalPages] = useState(1);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const limit = 20;
  
  // Track in-flight requests to prevent duplicate calls
  const fetchingRef = useRef(false);

  const fetchVideos = useCallback(async (pageNum: number = 1, forceRefresh: boolean = false) => {
    // Prevent duplicate requests
    if (fetchingRef.current) {
      console.log(`[${categoryLabel}] Request already in progress, skipping duplicate call`);
      return;
    }
    
    fetchingRef.current = true;
    try {
      setLoading(true);
      
      let response;
      if (category === VideoCategory.LEVEL_UP) {
        // For Level Up on main community page, fetch both published and draft series
        // This ensures we show series that might be in draft state when user creates them
        const [publishedResponse, draftResponse] = await Promise.all([
          // Fetch videos with published mediaStatus and published seriesStatus
          communityService.getVideosLevelUp(
            pageNum,
            limit,
            undefined, // status
            forceRefresh,
            undefined, // search
            undefined, // tags
            'published', // mediaStatus
            'published' // seriesStatus
          ),
          // Fetch videos with draft mediaStatus and draft seriesStatus
          communityService.getVideosLevelUp(
            pageNum,
            limit,
            undefined, // status
            forceRefresh,
            undefined, // search
            undefined, // tags
            'draft', // mediaStatus
            'draft' // seriesStatus
          )
        ]);
        
        // Merge videos from both responses, removing duplicates by _id
        const publishedVideos = publishedResponse?.data?.videos || [];
        // Mark videos from draft API call as draft status (they have draft mediaStatus and draft seriesStatus)
        const draftVideos = (draftResponse?.data?.videos || []).map(video => ({
          ...video,
          status: 'draft' // Override status to draft since they came from draft API call
        }));
        
        // Create a map to track videos by ID, prioritizing published over draft
        const videoMap = new Map<string, CommunityVideo>();
        
        // First add draft videos
        draftVideos.forEach(video => {
          videoMap.set(video._id, video);
        });
        
        // Then add published videos (will overwrite draft versions if duplicate)
        publishedVideos.forEach(video => {
          videoMap.set(video._id, video);
        });
        
        // Convert map back to array
        const uniqueVideos = Array.from(videoMap.values());
        
        // Sort by createdAt descending (most recent first)
        uniqueVideos.sort((a, b) => {
          const dateA = new Date(a.createdAt || a.updatedAt || 0).getTime();
          const dateB = new Date(b.createdAt || b.updatedAt || 0).getTime();
          return dateB - dateA;
        });
        
        // Calculate total pages based on combined results
        const totalPublished = publishedResponse?.data?.total || 0;
        const totalDraft = draftResponse?.data?.total || 0;
        const combinedTotal = totalPublished + totalDraft;
        const combinedTotalPages = Math.ceil(combinedTotal / limit);
        
        // For the main listing page, show the first 'limit' items from merged results
        // If pagination is needed, slice accordingly
        const startIndex = (pageNum - 1) * limit;
        const endIndex = startIndex + limit;
        const paginatedVideos = uniqueVideos.slice(startIndex, endIndex);
        
        if (pageNum === 1 || forceRefresh) {
          // Always replace videos on first page or force refresh
          setVideos(paginatedVideos);
        } else {
          // Append videos for pagination
          setVideos(prev => [...prev, ...paginatedVideos]);
        }
        
        setTotalPages(combinedTotalPages);
      } else {
        // Use /videos API for Action and Social (show published only on main page)
        // Pass undefined for search, tags, and featured filters (not used on main page)
        response = await communityService.getVideosByCategory(
          category,
          pageNum,
          limit,
          'published',
          undefined, // search
          undefined, // tags
          undefined, // featured
          forceRefresh
        );
        
        if (response?.data) {
          const videos = response.data.videos || [];
          
          if (pageNum === 1 || forceRefresh) {
            // Always replace videos on first page or force refresh
            setVideos(videos);
          } else {
            // Append videos for pagination
            setVideos(prev => [...prev, ...videos]);
          }
          
          setTotalPages(response.data.totalPages || 1);
        } else {
          // No data in response, set empty array
          setVideos([]);
        }
      }
    } catch (error) {
      console.error(`Error fetching ${categoryLabel} videos:`, error);
      setVideos([]);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [category, categoryLabel, limit]);

  useEffect(() => {
    setPage(1);
    setVideos([]);
    fetchVideos(1, false);
  }, [category, fetchVideos]);

  // Refresh when refreshKey changes
  useEffect(() => {
    if (refreshKey !== undefined && refreshKey > 0) {
      // Reset pagination and clear existing videos
      setPage(1);
      setVideos([]);
      
      // Fetch fresh data immediately with cache-busting
      fetchVideos(1, true);
    }
  }, [refreshKey, fetchVideos]);

  const navigate = useNavigate();
  
  const handleViewAll = () => {
    // Navigate to category-specific listing page
    const categoryPath = category === VideoCategory.ACTION ? 'action' : 
                        category === VideoCategory.LEVEL_UP ? 'level_up' : 
                        'social';
    navigate(`/community/${categoryPath}`);
  };

  const handleToggleMenu = (videoId: string) => {
    setOpenMenuId(openMenuId === videoId ? null : videoId);
  };

  const handleCloseMenu = () => {
    setOpenMenuId(null);
  };

  if (loading && videos.length === 0) {
    return (
      <div className="community-section">
        <div className="section-header">
          <h3 className="section-title">{categoryLabel}</h3>
        </div>
        <Row className="community-videos-row">
          {[...Array(6)].map((_, index) => (
            <Col key={index} xs={12} sm={6} md={4} lg={3} xl={2}>
              <div className="shimmer-card">
                <div className="shimmer-image" />
                <div className="shimmer-content">
                  <div className="shimmer-line" style={{ width: '60%' }} />
                  <div className="shimmer-line" style={{ width: '80%' }} />
                  <div className="shimmer-line" style={{ width: '40%' }} />
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </div>
    );
  }

  if (videos.length === 0 && !loading) {
    return (
      <div className="community-section" data-category={category}>
        <div className="section-header">
          <h3 className="section-title">{categoryLabel}</h3>
          <div className="section-actions">
            <button 
              className="add-new-btn"
              onClick={() => onAddNew?.(category)}
            >
              Add New +
            </button>
          </div>
        </div>
        <div className="text-center py-5">
          <div className="empty-state">
            <div className="empty-state-icon">
              <i className="ri-video-line" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
            </div>
            <div className="empty-state-title">No {categoryLabel} Videos Found</div>
            <div className="empty-state-description">
              There are no videos available in the {categoryLabel} category. Click "Add New +" to create your first video.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="community-section" data-category={category}>
      <div className="section-header">
        <h3 className="section-title">{categoryLabel}</h3>
        <div className="section-actions">
          <button onClick={handleViewAll} className="view-all-btn">
            View all
          </button>
          <button 
            className="add-new-btn"
            onClick={() => onAddNew?.(category)}
          >
            Add New +
          </button>
        </div>
      </div>
      
      <Row className="community-videos-row">
        {videos.map((video) => {
          // Render different card types based on category
          let cardComponent;
          let colSize;
          
          if (category === VideoCategory.ACTION) {
            colSize = { xs: 12, sm: 6, md: 4, lg: 3, xl: 2 };
            cardComponent = (
              <ActionCard
                video={video}
                onEdit={onEditVideo}
                onUnpublish={onUnpublishVideo}
                onDelete={onDeleteVideo}
                isMenuOpen={openMenuId === video._id}
                onToggleMenu={() => handleToggleMenu(video._id)}
                onCloseMenu={handleCloseMenu}
              />
            );
          } else if (category === VideoCategory.LEVEL_UP) {
            colSize = { xs: 12, sm: 12, md: 6, lg: 6 };
            // For Level Up series and standalone, don't show Unpublish option - only Edit and Delete
            const isLevelUpSeries = video.videoType === 'series';
            const isStandaloneLevelUp = video.videoType === 'single' || (video.contentType === 'long_form' && video.videoType !== 'series' && !video.seriesId);
            const shouldHideUnpublish = isLevelUpSeries || isStandaloneLevelUp;
            // For Level Up, show Publish/Unpublish based on video status (only for chapters, not series or standalone)
            const isPublished = video.status === 'published' || video.status === 'live';
            const isDraft = video.status === 'draft' || video.status === 'pending_review' || video.status === 'scheduled' || video.status === 'rejected';
            cardComponent = (
              <LevelUpCard
                video={video}
                onEdit={onEditVideo}
                onPublish={isDraft ? onPublishVideo : undefined}
                onUnpublish={shouldHideUnpublish ? undefined : (isPublished ? onUnpublishVideo : undefined)}
                onDelete={onDeleteVideo}
                isMenuOpen={openMenuId === video._id}
                onToggleMenu={() => handleToggleMenu(video._id)}
                onCloseMenu={handleCloseMenu}
              />
            );
          } else if (category === VideoCategory.SOCIAL) {
            colSize = { xs: 12, sm: 6, md: 4, lg: 3 };
            cardComponent = (
              <SocialCard
                video={video}
                onEdit={onEditVideo}
                onUnpublish={onUnpublishVideo}
                onDelete={onDeleteVideo}
                isMenuOpen={openMenuId === video._id}
                onToggleMenu={() => handleToggleMenu(video._id)}
                onCloseMenu={handleCloseMenu}
              />
            );
          }
          
          return (
            <Col key={video._id} {...colSize}>
              {cardComponent}
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default CommunitySection;

